/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { companyIdOf, stripSqlId } from './entityGroups'
import { useSmartGroups } from './useAuditLookups'

/**
 * A smart group shown by name, e.g. "FIT Static", falling back to `#1065` when it cannot be
 * resolved — still loading, from a company the viewer cannot see, or never named.
 *
 * The group's own company decides the lookup (`includedEntityGroup.companyId`); a field-level
 * edit often carries only the id, so the record's company stands in for it then.
 */
export const SmartGroupName: React.FC<{ id: unknown; company?: unknown; fallbackCompanyId: number }> = ({
  id,
  company,
  fallbackCompanyId,
}) => {
  const groupId = stripSqlId(id)
  const { data: groups, isLoading } = useSmartGroups(companyIdOf(company) ?? fallbackCompanyId)
  const group = groups?.get(groupId)

  // Inline rather than a panel-wide wait: the name sits inside an otherwise ready sentence.
  if (isLoading) return <span className="skeleton inline-block h-4 w-24 align-middle" aria-label="Loading name" />

  if (!group) return <>#{groupId}</>
  return (
    <span title={`Smart group #${groupId}`}>
      {group.name}
      {group.deleted && <span className="ml-1 text-base-content/50">(deleted)</span>}
    </span>
  )
}
